import React from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Box,
  Typography,
  Chip,
  Alert,
  FormHelperText,
  Stack
} from '@mui/material';
import {
  Business as OrganizationIcon,
  AccountTree as NetworkIcon,
  Security as SecurityIcon,
  Visibility as ViewIcon,
  Edit as EditIcon
} from '@mui/icons-material';

const PermissionScopeSelector = ({ value, onChange, error, role }) => {
  const handleChange = (event) => {
    onChange(event.target.value);
  };

  const getCapabilityText = (scope, userRole) => {
    const isInspector = userRole === 'inspector';
    const baseActions = isInspector 
      ? 'create/edit inspections, record defects, and manage' 
      : 'view inspections, defects, and access';

    if (scope === 'organization') {
      return `Can ${baseActions} ALL data within the organization`;
    } else {
      return `Can ${baseActions} ONLY assigned networks/sections`;
    }
  };

  return (
    <FormControl component="fieldset" error={!!error} fullWidth>
      <FormLabel component="legend" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SecurityIcon />
          <Typography variant="subtitle1" fontWeight={600}>Permission Scope</Typography>
        </Stack>
      </FormLabel>
      
      <RadioGroup
        value={value}
        onChange={handleChange}
        sx={{ gap: 2 }}
      >
        {/* Organization Level */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 3,
            border: value === 'organization' ? 2 : 1,
            borderColor: value === 'organization' ? 'primary.main' : 'divider',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
          onClick={() => onChange('organization')}
        >
          <FormControlLabel
            value="organization"
            control={<Radio />}
            label={
              <Box sx={{ ml: 1, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <OrganizationIcon color="success" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Organization-Level Access
                  </Typography>
                  <Chip 
                    label="Recommended" 
                    size="small" 
                    color="success" 
                    variant="outlined" 
                  />
                </Stack>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {getCapabilityText('organization', role)}
                </Typography>
                
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
                  <Chip 
                    icon={role === 'inspector' ? <EditIcon /> : <ViewIcon />} 
                    label="All Networks" 
                    size="small" 
                    variant="outlined" 
                  />
                  <Chip 
                    icon={role === 'inspector' ? <EditIcon /> : <ViewIcon />} 
                    label="All Sections" 
                    size="small" 
                    variant="outlined" 
                  />
                  <Chip 
                    icon={role === 'inspector' ? <EditIcon /> : <ViewIcon />} 
                    label="All Assets" 
                    size="small" 
                    variant="outlined" 
                  />
                </Stack>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    User will automatically get access to new networks and sections added to the organization.
                  </Typography>
                </Alert>
              </Box>
            }
            sx={{ alignItems: 'flex-start', m: 0 }}
          />
        </Paper>

        {/* Network/Section Level */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 3,
            border: value === 'network_section' ? 2 : 1,
            borderColor: value === 'network_section' ? 'primary.main' : 'divider',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
          onClick={() => onChange('network_section')}
        >
          <FormControlLabel
            value="network_section"
            control={<Radio />}
            label={
              <Box sx={{ ml: 1, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <NetworkIcon color="secondary" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Network/Section-Level Access
                  </Typography>
                  <Chip 
                    label="Granular Control" 
                    size="small" 
                    color="secondary" 
                    variant="outlined" 
                  />
                </Stack>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {getCapabilityText('network_section', role)}
                </Typography>
                
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
                  <Chip 
                    icon={<NetworkIcon />} 
                    label="Specific Networks" 
                    size="small" 
                    variant="outlined" 
                    color="secondary"
                  />
                  <Chip 
                    icon={<NetworkIcon />} 
                    label="Specific Sections" 
                    size="small" 
                    variant="outlined" 
                    color="secondary"
                  />
                  <Chip 
                    icon={role === 'inspector' ? <EditIcon /> : <ViewIcon />} 
                    label="Limited Assets" 
                    size="small" 
                    variant="outlined" 
                    color="secondary"
                  />
                </Stack>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    You will need to manually assign specific networks and sections. 
                    User won't see other areas of the organization.
                  </Typography>
                </Alert>
              </Box>
            }
            sx={{ alignItems: 'flex-start', m: 0 }}
          />
        </Paper>
      </RadioGroup>

      {error && <FormHelperText>{error}</FormHelperText>}

      {/* Additional Information */}
      <Paper sx={{ mt: 3, p: 3, backgroundColor: 'background.paper', borderRadius: 1 }} variant="outlined">
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          💡 Quick Guide
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          <strong>Organization-Level:</strong> Best for users who need broad access across your entire organization. 
          Easier to manage and automatically includes new areas.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Network/Section-Level:</strong> Best for contractors, temporary staff, or users who should only 
          access specific parts of your infrastructure.
        </Typography>
      </Paper>
    </FormControl>
  );
};

export default PermissionScopeSelector; 